import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "../common/table/Table";
import { routeNames } from "../../consts/routesNames";
import { PERMISSIONS } from "../../auth/utils/permissionCodes";
import { useAuth, usePermissions } from "../../auth/hooks/usePermissions";
import { mapContactLinkedUserFields } from "../../auth/utils/contactLink";
import { ENTITY_LINKS_TABLE_EMPTY } from "../../consts/tableEmptyMessages";
import { formatPropertyFullAddress } from "../../utils/tableDisplayUtils";
import "./EntityLinksSection.css";

function mapPropertyRows(list) {
  return (list || []).map((property) => ({
    ...property,
    address: formatPropertyFullAddress(property),
    type_name: typeof property.type === "string" ? property.type : (property.type?.name ?? property.type_name ?? "—"),
    status: property.status ?? "—",
  }));
}

function mapCompanyRows(list) {
  return (list || []).map((company) => ({
    ...company,
    category_name: company.category_name ?? (typeof company.category === "object" ? company.category?.name : company.category) ?? "—",
    email: company.email != null && String(company.email).trim() !== "" ? company.email : "—",
    name: company.name?.trim() || "—",
  }));
}

function mapContactRows(list) {
  return (list || []).map((contact) => ({
    ...contact,
    full_name: `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim() || "—",
    company_name: contact.company_name ?? (typeof contact.company === "object" ? contact.company?.name : "") ?? "—",
    category_name: contact.category_name ?? (typeof contact.category === "object" ? contact.category?.name : contact.category) ?? "—",
    email: contact.email?.trim() || "—",
    ...mapContactLinkedUserFields(contact),
  }));
}

const PROPERTY_HEADERS = [
  { key: "address", name: "Full Address" },
  { key: "status", name: "Status" },
  { key: "type_name", name: "Project Type" },
];

const COMPANY_HEADERS = [
  { key: "name", name: "Name" },
  { key: "category_name", name: "Category" },
  { key: "email", name: "Email" },
];

const CONTACT_HEADERS_BASE = [
  { key: "full_name", name: "Name" },
  { key: "company_name", name: "Company Name" },
  { key: "email", name: "Email" },
  { key: "category_name", name: "Category" },
];

function EntityLinksSection({ relatedProperties, relatedCompanies, relatedContacts, loading }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { isAdmin } = useAuth();

  const canViewProperties = hasPermission(PERMISSIONS.PROPERTIES.VIEW);
  const canViewCompanies = hasPermission(PERMISSIONS.COMPANIES.VIEW);
  const canViewContacts = hasPermission(PERMISSIONS.CONTACTS.VIEW);

  const propertyRows = useMemo(() => mapPropertyRows(relatedProperties), [relatedProperties]);
  const companyRows = useMemo(() => mapCompanyRows(relatedCompanies), [relatedCompanies]);
  const contactRows = useMemo(() => mapContactRows(relatedContacts), [relatedContacts]);

  const contactHeaders = useMemo(() => {
    const headers = [...CONTACT_HEADERS_BASE];
    if (isAdmin) {
      headers.push({ key: "linked_user_name", name: "Linked User" });
    }
    return headers;
  }, [isAdmin]);

  if (!canViewProperties && !canViewCompanies && !canViewContacts) {
    return null;
  }

  const handlePropertyClick = (row) => {
    navigate(`${routeNames.PROPERTY_DETAILS}?id=${row.id}`, { state: { data: row } });
  };

  const handleCompanyClick = (row) => {
    navigate(`${routeNames.COMPANY_DETAILS}?id=${row.id}`, { state: { data: row } });
  };

  const handleContactClick = (row) => {
    navigate(`${routeNames.CONTACT_DETAILS}?id=${row.id}`, { state: { data: row } });
  };

  return (
    <div className="entity-links-root">
      <h3 className="property-details-profile-notes-heading entity-links-main-heading">
        <strong>Links:</strong>
      </h3>
      {loading ? (
        <div className="entity-links-spinner-wrap" role="status" aria-live="polite" aria-label="Loading links">
          <CircularProgress size={36} thickness={4} />
        </div>
      ) : (
        <>
          {canViewProperties && (
            <>
              <h4 className="property-tasks-subsection-heading entity-links-subsection-heading">Projects</h4>
              <div className="table-main-container-entity-links">
                <Table
                  searchable={true}
                  tableHeight="400px"
                  headers={PROPERTY_HEADERS}
                  rows={propertyRows}
                  onRowClick={handlePropertyClick}
                  emptyMessage={ENTITY_LINKS_TABLE_EMPTY.PROPERTIES}
                />
              </div>
            </>
          )}
          {canViewCompanies && (
            <>
              <h4 className="property-tasks-subsection-heading entity-links-subsection-heading entity-links-subsection-heading--companies">
                Companies
              </h4>
              <div className="table-main-container-entity-links">
                <Table
                  searchable={true}
                  tableHeight="400px"
                  headers={COMPANY_HEADERS}
                  rows={companyRows}
                  onRowClick={handleCompanyClick}
                  emptyMessage={ENTITY_LINKS_TABLE_EMPTY.COMPANIES}
                />
              </div>
            </>
          )}
          {canViewContacts && (
            <>
              <h4 className="property-tasks-subsection-heading entity-links-subsection-heading entity-links-subsection-heading--contacts">
                Contacts
              </h4>
              <div className="table-main-container-entity-links">
                <Table
                  searchable={true}
                  tableHeight="400px"
                  headers={contactHeaders}
                  rows={contactRows}
                  onRowClick={handleContactClick}
                  emptyMessage={ENTITY_LINKS_TABLE_EMPTY.CONTACTS}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

EntityLinksSection.propTypes = {
  relatedProperties: PropTypes.arrayOf(PropTypes.object),
  relatedCompanies: PropTypes.arrayOf(PropTypes.object),
  relatedContacts: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
};

EntityLinksSection.defaultProps = {
  relatedProperties: [],
  relatedCompanies: [],
  relatedContacts: [],
  loading: false,
};

export default EntityLinksSection;
